import { Fragment, type ReactNode } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { X } from 'lucide-react';

import { cn } from '../../lib/cn';
import IconButton from './IconButton';

/**
 * Modal — Agent Builder's modal pattern (matches `src/components/ui/Modal.jsx`):
 *   w-full max-w-xl rounded-lg border shadow-2xl
 *   header: bg-gray-100 / dark:bg-gray-800/50 with bottom border
 *   body:   px-6 py-5 scrollable
 *   footer: bg-gray-100 / dark:bg-gray-800/60 with top border
 *
 * Uses HeadlessUI for focus trap + ESC + transitions (matches agent-builder
 * usage of @headlessui/react v2).
 */
interface ModalProps {
    open: boolean;
    onClose: () => void;
    title: string;
    children: ReactNode;
    /** Constrain width — defaults max-w-xl */
    widthClass?: string;
    /** Optional footer (action buttons row) */
    footer?: ReactNode;
    isDarkMode?: boolean;
}

export default function Modal({
    open,
    onClose,
    title,
    children,
    widthClass = 'max-w-xl',
    footer,
    isDarkMode = false,
}: ModalProps) {
    return (
        <Transition show={open} as={Fragment}>
            <Dialog onClose={onClose} className="relative z-50">
                {/* Backdrop */}
                <Transition.Child
                    as={Fragment}
                    enter="ease-out duration-200"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="ease-in duration-150"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                >
                    <div
                        aria-hidden="true"
                        className={cn(
                            'fixed inset-0 backdrop-blur-sm',
                            isDarkMode ? 'bg-gray-900/80' : 'bg-gray-900/60',
                        )}
                    />
                </Transition.Child>

                {/* Centered panel */}
                <div className="fixed inset-0 flex items-center justify-center p-4">
                    <Transition.Child
                        as={Fragment}
                        enter="ease-out duration-200"
                        enterFrom="opacity-0 scale-95"
                        enterTo="opacity-100 scale-100"
                        leave="ease-in duration-150"
                        leaveFrom="opacity-100 scale-100"
                        leaveTo="opacity-0 scale-95"
                    >
                        <Dialog.Panel
                            className={cn(
                                'w-full max-h-[calc(100vh-40px)] rounded-lg border shadow-2xl overflow-hidden flex flex-col',
                                widthClass,
                                isDarkMode
                                    ? 'bg-gray-900 border-gray-700/70'
                                    : 'bg-white border-gray-200',
                            )}
                        >
                            {/* Header */}
                            <div
                                className={cn(
                                    'px-6 py-4 flex items-center justify-between border-b',
                                    isDarkMode
                                        ? 'bg-gray-800/50 border-gray-700/70'
                                        : 'bg-gray-100 border-gray-200',
                                )}
                            >
                                <Dialog.Title
                                    className={cn(
                                        'text-base font-semibold',
                                        isDarkMode ? 'text-gray-100' : 'text-gray-900',
                                    )}
                                >
                                    {title}
                                </Dialog.Title>
                                <IconButton
                                    icon={<X className="h-4 w-4" />}
                                    label="Close"
                                    onClick={onClose}
                                    isDarkMode={isDarkMode}
                                    size="sm"
                                />
                            </div>

                            {/* Body */}
                            <div
                                className={cn(
                                    'px-6 py-5 overflow-y-auto',
                                    isDarkMode ? 'text-gray-300' : 'text-gray-700',
                                )}
                            >
                                {children}
                            </div>

                            {/* Footer */}
                            {footer && (
                                <div
                                    className={cn(
                                        'px-6 py-4 flex justify-end gap-2 border-t',
                                        isDarkMode
                                            ? 'bg-gray-800/60 border-gray-700/70'
                                            : 'bg-gray-100 border-gray-200',
                                    )}
                                >
                                    {footer}
                                </div>
                            )}
                        </Dialog.Panel>
                    </Transition.Child>
                </div>
            </Dialog>
        </Transition>
    );
}
